export type ArticulationStance = {
  id: string;
  label: string;
  line: string;
  landing: string;
  protects: string;
  tradeoff: string;
};

export type ExpressionIntention =
  | "hold-ground"
  | "say-no"
  | "correct-with-care"
  | "ask-or-persuade"
  | "say-a-hard-thing"
  | "own-a-mistake"
  | "be-concise"
  | "answer-pressure"
  | "restore-understanding";

export type ExpressionRelationship =
  | "work-peer"
  | "group"
  | "someone-close"
  | "person-you-need-to-move"
  | "person-you-lead"
  | "new-connection";

export type ExpressionRisk =
  | "hostility"
  | "vagueness"
  | "humiliation"
  | "coercion"
  | "self-erasure"
  | "lost-meaning"
  | "sarcasm"
  | "false-certainty";

export type ExpressionRoute = {
  intention: ExpressionIntention;
  relationship: ExpressionRelationship;
  risk: ExpressionRisk;
};

export type LandingHazard = {
  label: string;
  accidentalReading: string;
  deliveryCue: string;
  saferAlternative: string;
};

export const EXPRESSION_INTENTIONS: ReadonlyArray<{ id: ExpressionIntention; label: string }> = [
  { id: "hold-ground", label: "Hold my ground" },
  { id: "say-no", label: "Say no clearly" },
  { id: "correct-with-care", label: "Correct without crushing" },
  { id: "ask-or-persuade", label: "Ask or persuade" },
  { id: "say-a-hard-thing", label: "Say a hard thing" },
  { id: "own-a-mistake", label: "Own a mistake" },
  { id: "be-concise", label: "Use fewer words" },
  { id: "answer-pressure", label: "Answer pressure lightly" },
  { id: "restore-understanding", label: "Repair understanding" },
];

export const EXPRESSION_RELATIONSHIPS: ReadonlyArray<{ id: ExpressionRelationship; label: string }> = [
  { id: "work-peer", label: "One person at work" },
  { id: "group", label: "A group or room" },
  { id: "someone-close", label: "Someone close" },
  { id: "person-you-need-to-move", label: "Someone whose choice matters" },
  { id: "person-you-lead", label: "Someone you lead" },
  { id: "new-connection", label: "Someone new" },
];

export const EXPRESSION_RISKS: ReadonlyArray<{ id: ExpressionRisk; label: string }> = [
  { id: "hostility", label: "Sounding hostile" },
  { id: "vagueness", label: "Sounding vague" },
  { id: "humiliation", label: "Diminishing someone" },
  { id: "coercion", label: "Sounding coercive" },
  { id: "self-erasure", label: "Making myself smaller" },
  { id: "lost-meaning", label: "Losing the real point" },
  { id: "sarcasm", label: "Accidental sarcasm" },
  { id: "false-certainty", label: "Pretending to know" },
];

export type ExpressionStudy = {
  id: string;
  contentVersion: number;
  number: string;
  title: string;
  provenance: "Original Verbalore";
  craft: string[];
  size: "quick" | "standard" | "deep";
  minutes: number;
  context: string;
  skill: string;
  situation: string;
  tangledDraft: string;
  draftDiagnosis: string;
  intention: string;
  invariant: string;
  concept: string;
  practiceCue: string;
  route: ExpressionRoute;
  landingHazard: LandingHazard;
  stances: ArticulationStance[];
};

export const BOUNDARY_STUDY: ExpressionStudy = {
  id: "boundary-not-favour",
  contentVersion: 2,
  number: "01",
  title: "A boundary, not a favour",
  provenance: "Original Verbalore",
  craft: ["compression", "reciprocity", "controlled authority"],
  size: "quick",
  minutes: 3,
  context: "Workplace conversation",
  skill: "Protecting a turn",
  route: { intention: "hold-ground", relationship: "work-peer", risk: "sarcasm" },
  landingHazard: {
    label: "Courtesy can turn into accidental sarcasm",
    accidentalReading: "The over-polite draft can sound as if you are mocking the idea that you need permission, even when you only meant to soften the boundary.",
    deliveryCue: "Keep your pace even. Stress finish, not okay, and do not smile on the permission phrase.",
    saferAlternative: "I’ll complete the thought, then I want to hear your response.",
  },
  situation:
    "A colleague repeatedly interrupts you in a meeting. You want to finish your point without sounding hostile or asking permission to speak.",
  tangledDraft:
    "Could I finish this point before we move on?",
  draftDiagnosis:
    "This is courteous and completely usable. Its weakness is smaller: it asks the interrupter for permission to complete a turn you already hold. The revision should keep the courtesy while stating the order clearly.",
  intention: "Protect your turn while leaving room for the other person to respond.",
  invariant: "You will finish the point first. Their response still matters.",
  concept: "Confidence is not volume. It is clarity without unnecessary permission.",
  practiceCue: "State the order of turns in one line while keeping the response welcome.",
  stances: [
    {
      id: "direct",
      label: "Direct",
      line: "Let me finish the point before you respond.",
      landing: "Clear and immediate",
      protects: "The order of turns is unmistakable.",
      tradeoff: "It leaves little warmth in the moment.",
    },
    {
      id: "tactful",
      label: "Tactful",
      line: "I’ll complete the thought, then I want to hear your response.",
      landing: "Firm with reciprocity",
      protects: "The boundary and the other person’s dignity remain visible.",
      tradeoff: "The second clause makes it slightly less compressed.",
    },
    {
      id: "warm-authority",
      label: "Warm authority",
      line: "Give me a moment to finish this, and then I’m with you.",
      landing: "Human and composed",
      protects: "Connection survives the correction.",
      tradeoff: "Warmth can soften the firmness if your delivery wavers.",
    },
    {
      id: "restrained",
      label: "Restrained",
      line: "I’ll finish the point first. Your response comes next.",
      landing: "Quiet order",
      protects: "The sequence stays firm without commenting on the interruption.",
      tradeoff: "Its formality can cool a collaborative room.",
    },
    {
      id: "punchy",
      label: "Punchy",
      line: "Let me land the point. Then it’s yours.",
      landing: "Brief and memorable",
      protects: "The sequence carries force without a long explanation.",
      tradeoff: "Its idiom may feel too sharp in a formal room.",
    },
  ],
};

export const HOMEPAGE_RANGE = {
  provenance: "Original Verbalore",
  intention: "Tell a colleague their draft cannot ship yet and needs another pass.",
  invariant: "The draft is not ready. More work is required. You are making the call.",
  stances: [
    {
      id: "direct",
      label: "Direct",
      line: "This isn’t ready. It needs another pass before we ship.",
      landing: "The verdict arrives without delay.",
    },
    {
      id: "tactful",
      label: "Tactful",
      line: "This needs another pass before it is ready to ship.",
      landing: "The work is corrected without diminishing its author.",
    },
    {
      id: "warm-authority",
      label: "Warm authority",
      line: "I want us to ship this with confidence. It needs another pass before I can approve it.",
      landing: "Encouragement carries the decision instead of replacing it.",
    },
    {
      id: "restrained",
      label: "Restrained",
      line: "We are not there yet. It needs another round before it ships.",
      landing: "The refusal stays measured and leaves little friction.",
    },
    {
      id: "punchy",
      label: "Punchy",
      line: "Not yet. Another pass before this can ship.",
      landing: "Compression gives the decision momentum.",
    },
  ],
} as const;

export const EXPRESSION_STUDIES: ExpressionStudy[] = [
  {
    id: "disagree-without-a-fight",
    contentVersion: 2,
    number: "02",
    title: "Disagree without a fight",
    provenance: "Original Verbalore",
    craft: ["dissent", "position-holding", "non-escalation"],
    size: "standard",
    minutes: 7,
    context: "A launch decision in a team meeting",
    skill: "Opposing a proposal with a concrete reason",
    route: { intention: "hold-ground", relationship: "group", risk: "hostility" },
    landingHazard: {
      label: "A position can sound like a verdict on the person",
      accidentalReading: "A clipped disagreement may be heard as contempt for the speaker rather than a serious objection to the proposal.",
      deliveryCue: "Name the proposal or consequence, not the person. Keep the reason in the same calm breath as the disagreement.",
      saferAlternative: "I see why Friday matters. I still think the accessibility review must happen before launch.",
    },
    situation:
      "The team proposes launching on Friday without the planned accessibility review. You believe skipping it could exclude users and create avoidable rework. You want to oppose the plan without attacking the person who proposed it.",
    tangledDraft:
      "I don’t think this is the right approach.",
    draftDiagnosis:
      "The disagreement is clear, but right approach sounds like a verdict without evidence. Name the accessibility review and the consequence of skipping it so the room can examine the plan instead of defending its author.",
    intention: "Oppose a Friday launch without accessibility review while keeping the disagreement focused on user and delivery risk.",
    invariant: "You do not support launching before accessibility review. The risks of exclusion and rework remain visible, and the proposer is not attacked.",
    concept: "Disagreement is information, not an attack. Naming it clearly is a form of respect.",
    practiceCue: "Read each line and delete any word that apologises for disagreeing. The objection should survive the cut.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I don’t support a Friday launch without the accessibility review. It risks excluding users and creating rework.",
        landing: "Plain and unhedged",
        protects: "Your actual position goes on the record without spin.",
        tradeoff: "It offers no cushion for someone expecting consensus.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I see why Friday matters. I still think we should complete the accessibility review before launch; the cost of missing it is higher than the delay.",
        landing: "Heard, then held",
        protects: "The other view is acknowledged before yours arrives.",
        tradeoff: "The softening reads as hedging if you rush the reason.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I want us to launch with confidence. Let’s complete the accessibility review first, then set the date from what we find.",
        landing: "Loyal opposition",
        protects: "The dissent reads as investment, not opposition.",
        tradeoff: "Warmth can blur how firm the objection really is.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "I can’t support launching before the accessibility review is complete.",
        landing: "Quietly unmoved",
        protects: "You hold the position without opening an argument.",
        tradeoff: "The reasons stay implied, so it can be brushed aside.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Accessibility first. Then launch.",
        landing: "Short and certain",
        protects: "It leaves no doubt where you stand.",
        tradeoff: "Without the reasoning it can feel like a wall.",
      },
    ],
  },
  {
    id: "a-no-that-stays-no",
    contentVersion: 2,
    number: "03",
    title: "A no that stays no",
    provenance: "Original Verbalore",
    craft: ["refusal", "clean refusal", "concision"],
    size: "standard",
    minutes: 6,
    context: "A colleague asks you to take over their client summary",
    skill: "Declining a concrete request without a false maybe",
    route: { intention: "say-no", relationship: "work-peer", risk: "vagueness" },
    landingHazard: {
      label: "Kindness can accidentally sound like a maybe",
      accidentalReading: "Explanations and apologies after the no can make the listener believe the request is still negotiable.",
      deliveryCue: "Say the refusal once, let the final word fall, and allow a short silence instead of filling it.",
      saferAlternative: "I can’t take this on, but I can point you to who can.",
    },
    situation:
      "A colleague asks you to write the client summary they own by tomorrow. Your schedule is already committed, and taking it would displace work you promised to deliver. You want to decline without turning a real no into a maybe.",
    tangledDraft:
      "I’m already stretched this week, but send it over and I’ll see what I can do.",
    draftDiagnosis:
      "This sounds considerate, but send it over and I’ll see turns a no into an assignment followed by negotiation. If the answer is no, let the refusal arrive before any explanation or alternative.",
    intention: "Decline writing the client summary clearly so the colleague can make another plan.",
    invariant: "You will not write the client summary. The answer is final and arrives early enough for another plan.",
    concept: "A clean no protects everyone’s time. A blurry no just books the same conversation twice.",
    practiceCue: "Say the no out loud, then stop. Notice the urge to keep talking, and let the silence finish it.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "No, I can’t take on the client summary.",
        landing: "Flat no",
        protects: "The answer is unmistakable.",
        tradeoff: "It gives the other person nothing to do next.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I can’t write the summary, but I can answer one specific question before four.",
        landing: "No, with a door",
        protects: "The no stands while a path forward stays open.",
        tradeoff: "The offer distracts if they hear only the second half.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I can’t take this one on, and I want to tell you now so you have time to plan.",
        landing: "Honest and kind",
        protects: "The honesty reads as respect for their time.",
        tradeoff: "The extra clause adds length to a simple answer.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "I’m not available to write the client summary.",
        landing: "Soft but final",
        protects: "It closes the request without any friction.",
        tradeoff: "Mild phrasing can invite a second ask.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "I can’t own the summary.",
        landing: "Quick and closed",
        protects: "The refusal and the reason land together.",
        tradeoff: "The idiom can feel curt in writing.",
      },
    ],
  },
  {
    id: "correct-dont-crush",
    contentVersion: 2,
    number: "04",
    title: "Correct, don’t crush",
    provenance: "Original Verbalore",
    craft: ["correction", "face-saving", "problem-focus"],
    size: "standard",
    minutes: 8,
    context: "A private correction before a client deck is sent",
    skill: "Naming a specific error and required fix without shrinking its author",
    route: { intention: "correct-with-care", relationship: "work-peer", risk: "humiliation" },
    landingHazard: {
      label: "A correction can become a public judgment",
      accidentalReading: "Even accurate words can sound humiliating when the room, timing, or emphasis makes the person rather than the error the subject.",
      deliveryCue: "Lower the volume, name the fix, and move quickly from what is wrong to what happens next.",
      saferAlternative: "There’s an error in the figures. Let’s fix it before this goes out.",
    },
    situation:
      "A teammate's client deck contains a total on page four that does not match the source figures. You are speaking to them privately before the deck is sent. You need the error corrected without turning the fix into a verdict on their competence.",
    tangledDraft:
      "I think you may have the figures wrong here. Can you fix them before we send?",
    draftDiagnosis:
      "The action is clear, but you have the figures wrong makes the person the grammatical subject of the failure. Put the error and the required correction in the foreground; responsibility can be handled without a public verdict.",
    intention: "Name the page-four total and have it corrected before send while leaving the teammate's standing intact.",
    invariant: "The page-four total is wrong, the teammate must correct it before send, and their competence is not put on trial.",
    concept: "Direct the pressure at what needs fixing, not at the person fixing it. Precision can be firm without becoming personal.",
    practiceCue: "For each line, ask whether it is hard on the problem or hard on the person. Keep the first, cut the second.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "The total on page four is wrong. Please correct it before we send the deck.",
        landing: "Problem first",
        protects: "The fix is the focus, not the blame.",
        tradeoff: "In front of others even a neutral tone can sting.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "Page four doesn’t match the source figures. Could you correct the total before we send it?",
        landing: "Shared second look",
        protects: "The correction lands as a check, not an accusation.",
        tradeoff: "The softening can understate how much it matters.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "Let’s get page four right before it goes out. Please check the total against the source and send me the corrected deck.",
        landing: "Shared standard, then correction",
        protects: "The shared goal is named before the additional work.",
        tradeoff: "Collaborative wording can blur who must act if responsibility is unclear.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "Please correct the page-four total before we send the deck.",
        landing: "Invited, not accused",
        protects: "It asks for the fix without assigning fault.",
        tradeoff: "The vagueness can slow the fix down.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Page four is off. Fix the total before we send it.",
        landing: "Urgency first",
        protects: "The required action is unmistakable.",
        tradeoff: "The clipped delivery can feel cold to someone already anxious.",
      },
    ],
  },
  {
    id: "say-what-you-want",
    contentVersion: 3,
    number: "05",
    title: "Ask for the swap",
    provenance: "Original Verbalore",
    craft: ["clear request", "de-hedging", "brevity"],
    size: "quick",
    minutes: 4,
    context: "Asking a colleague to exchange Friday and Monday sessions",
    skill: "Making a fair request clear enough to get a straight answer",
    route: { intention: "ask-or-persuade", relationship: "person-you-need-to-move", risk: "vagueness" },
    landingHazard: {
      label: "Politeness can hide the request",
      accidentalReading: "A listener may hear a general wish or complaint because the action and decision you need never become explicit.",
      deliveryCue: "Give the request one stressed verb and end with room for a real yes or no.",
      saferAlternative: "Could you cover my Friday session? I can take your Monday session in return, and it’s completely fine if you can’t.",
    },
    situation:
      "A medical appointment means you need a colleague to cover your Friday session. You can take their Monday session in return, and they remain free to decline. You want to make the exchange plainly enough to receive a real answer.",
    tangledDraft:
      "It would really help if somebody could cover Friday.",
    draftDiagnosis:
      "The need is visible but the request is not. Nobody is named, and nobody has been asked for an answer. Address the person, name the action, and leave room for a real yes or no.",
    intention: "Ask the colleague to cover Friday and offer to take Monday in return, without making refusal awkward.",
    invariant: "The Friday request and Monday exchange are explicit. The colleague remains free to say no.",
    concept: "A buried ask hands the other person the work of guessing. Clarity is a courtesy.",
    practiceCue: "Strip the qualifiers off the front of each line. Read what’s left and check it can still be answered yes or no.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "Can you cover Friday’s session? I can take Monday’s in return.",
        landing: "Straight ask",
        protects: "The request is specific and answerable.",
        tradeoff: "It leaves little room to save face if they’d rather not.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "Could you cover Friday’s session? If you can, I’ll take Monday’s; if not, I’ll ask elsewhere.",
        landing: "Clear, easy out",
        protects: "The ask stays clear while giving them an easy exit.",
        tradeoff: "The added reason can read as gentle pressure.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "Would you cover Friday for me? I’d be glad to take Monday in return.",
        landing: "An invitation",
        protects: "The ask feels like an opportunity, not a burden.",
        tradeoff: "Charm can obscure that it’s still a real request.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "Are you available to exchange your Monday session for my Friday one?",
        landing: "Plain question",
        protects: "A plain question, easy to answer either way.",
        tradeoff: "The understatement can hide how much you want a yes.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Can you take Friday? I’ll take Monday.",
        landing: "Nothing to decode",
        protects: "There is nothing to decode and it’s quick to answer.",
        tradeoff: "Too casual for a big or sensitive ask.",
      },
    ],
  },
  {
    id: "the-truth-whole",
    contentVersion: 2,
    number: "06",
    title: "The truth, whole",
    provenance: "Original Verbalore",
    craft: ["honesty", "care under truth", "candour"],
    size: "deep",
    minutes: 11,
    context: "Telling someone close a hard truth they asked for",
    skill: "Delivering the whole truth without softening it into a lie",
    route: { intention: "say-a-hard-thing", relationship: "someone-close", risk: "humiliation" },
    landingHazard: {
      label: "Candour can become a performance of bluntness",
      accidentalReading: "The listener may feel that your honesty is being used to display superiority rather than to answer the question they trusted you with.",
      deliveryCue: "Keep the voice warm, make the truth specific, and remove any phrase that congratulates you for being honest.",
      saferAlternative: "I care about you, so I’m going to give you the whole answer, not the comfortable half.",
    },
    situation:
      "A friend is pinning their hopes on a project that is failing, and they have asked whether more time will save it. Your honest assessment is that more time is unlikely to change the outcome. You want to tell them without cushioning the truth into a falsehood or delivering it coldly.",
    tangledDraft:
      "I think it needs more time before we decide whether it’s working.",
    draftDiagnosis:
      "This sounds supportive, but it changes your honest assessment. More time becomes a promise of possibility even though you believe time will not alter the outcome. Keep the care; restore the truth.",
    intention: "Tell the person the hard truth without softening it into something untrue.",
    invariant: "The project is failing, and more time is unlikely to change that. The assessment is not blurred into false hope.",
    concept: "Kindness lives in how you tell the truth, not in whether you tell it. Withholding it is its own harm.",
    practiceCue: "Say the truth first, then the care. If the care changes the truth, you’ve softened too far.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "It isn’t working, and I don’t think more time will change that.",
        landing: "Clean and whole",
        protects: "They get reality with nothing to misread.",
        tradeoff: "Without a pause it can land harder than you mean.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I care about you, so I’ll be honest: I don’t think this is going to work.",
        landing: "Cared for, then told",
        protects: "The relationship frames the truth instead of blunting it.",
        tradeoff: "The preamble can make them brace before the point arrives.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "You asked me straight, so here it is: this is failing, and more time is unlikely to change it.",
        landing: "Owed to you",
        protects: "Respect for them carries the bad news.",
        tradeoff: "The framing draws attention to you as the messenger.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "I don’t see this turning around, even with more time.",
        landing: "Quiet certainty",
        protects: "The truth stands without added weight.",
        tradeoff: "The quietness can let them minimize what you said.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "It isn’t working. More time is unlikely to change that.",
        landing: "Compressed assessment",
        protects: "The conclusion and its uncertainty both survive.",
        tradeoff: "The brevity can feel abrupt for something this heavy.",
      },
    ],
  },
  {
    id: "the-exact-feeling",
    contentVersion: 2,
    number: "07",
    title: "The exact feeling",
    provenance: "Original Verbalore",
    craft: ["emotional precision", "naming", "restraint"],
    size: "standard",
    minutes: 7,
    context: "Naming a feeling to someone close",
    skill: "Saying the exact feeling instead of a vague one",
    route: { intention: "say-a-hard-thing", relationship: "someone-close", risk: "vagueness" },
    landingHazard: {
      label: "A vague feeling can make the other person guess the charge",
      accidentalReading: "Broad words such as upset or weird can sound larger, more accusatory, or less honest than the exact feeling underneath them.",
      deliveryCue: "Stress the precise feeling and the moment that produced it. Do not inflate it with always or never.",
      saferAlternative: "I felt dismissed when the decision was made before I could answer.",
    },
    situation:
      "Someone close made a decision that affects you without asking you first. You feel hurt, not angry, and want to name both the feeling and its cause precisely enough that they can respond.",
    tangledDraft:
      "I was pretty upset about how that decision happened.",
    draftDiagnosis:
      "This is honest enough to begin, but upset and how it happened make the listener guess both the feeling and the cause. Name the more precise feeling and the exact moment that produced it.",
    intention: "Name what you actually feel precisely enough to be understood.",
    invariant: "You feel hurt because they made the decision without asking you. The feeling and cause remain intact.",
    concept: "A vague feeling earns a vague reply. Precision is what makes a feeling answerable.",
    practiceCue: "Replace the first feeling word you reach for with a more precise one. Notice how the response changes.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I’m not angry. I’m hurt that you didn’t ask me first.",
        landing: "Named and exposed",
        protects: "The exact feeling and its cause are both visible.",
        tradeoff: "The precision leaves you exposed.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I’ve been sitting with this, and I feel hurt that you decided without asking me.",
        landing: "Sat with, then said",
        protects: "Naming the process makes the feeling easier to receive.",
        tradeoff: "The lead-in can delay the point.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I want to be clear because it matters: I felt hurt when you decided without asking me.",
        landing: "Precise on purpose",
        protects: "The precision keeps it from escalating into the wrong fight.",
        tradeoff: "The framing can feel managed rather than raw.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "I felt hurt when you decided without asking me.",
        landing: "Quiet precision",
        protects: "The feeling and its cause remain clear without added framing.",
        tradeoff: "It does not yet tell the listener what repair you want.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Not angry. Hurt that you chose without me.",
        landing: "Three beats",
        protects: "The distinction lands in three beats.",
        tradeoff: "The compression can read as clipped when the moment is tender.",
      },
    ],
  },
  {
    id: "move-them-dont-push",
    contentVersion: 2,
    number: "08",
    title: "Move them, don’t push",
    provenance: "Original Verbalore",
    craft: ["persuasion", "honest persuasion", "freedom to refuse"],
    size: "standard",
    minutes: 8,
    context: "Moving someone toward a decision",
    skill: "Persuading by argument, leaving them free to refuse",
    route: { intention: "ask-or-persuade", relationship: "person-you-need-to-move", risk: "coercion" },
    landingHazard: {
      label: "Confidence can quietly become pressure",
      accidentalReading: "Urgency, certainty, or assumed agreement can make a proposal feel compulsory even when you intend to persuade honestly.",
      deliveryCue: "Make the case, name the limit, and leave the final decision grammatically and vocally with the listener.",
      saferAlternative: "Give this two weeks. We will limit the risk, learn from real evidence, and decide together after.",
    },
    situation:
      "You want someone to back a two-week pilot they are hesitant about. The pilot limits risk and gives both of you evidence before a larger commitment. You want to move them with that case, not pressure or flattery.",
    tangledDraft:
      "I really think the pilot is the right move, and I need you to trust me on this.",
    draftDiagnosis:
      "The confidence is useful, but trust me asks the relationship to carry evidence the proposal should carry. Name the limited commitment and what the pilot will prove, then leave the decision with the listener.",
    intention: "Ask the person to back a two-week pilot because it limits risk and produces evidence, while leaving them free to refuse.",
    invariant: "The two-week pilot, limited risk, and evidence remain the case. The listener remains free to say no.",
    concept: "Real persuasion leaves the other person free to say no. That freedom is what makes their yes worth having.",
    practiceCue: "After each line, imagine them saying no. If the line only works when they can’t, it’s pressure, not persuasion.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "Will you back a two-week pilot? It limits the risk and gives us evidence before we commit.",
        landing: "Case first",
        protects: "The argument stands on its own merits.",
        tradeoff: "Leading with logic can feel like a challenge.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I know you have doubts. Would you consider a two-week pilot so we can test it without a full commitment?",
        landing: "Doubt respected",
        protects: "Their hesitation is respected while you still make the case.",
        tradeoff: "The deference can cost you momentum.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I’d like your backing for two weeks. We will limit the risk, learn from real evidence, and decide together after.",
        landing: "Confident welcome",
        protects: "The confidence invites them in rather than cornering them.",
        tradeoff: "Assuming agreement can put a wary person on guard.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "Would you back a two-week pilot? It gives us evidence without committing us beyond it.",
        landing: "An open door",
        protects: "The actual request and the bounded case are both present without a push.",
        tradeoff: "The understatement can fail to convey how much it matters.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Two weeks. Limited risk. Real evidence. Then you decide.",
        landing: "Drive and freedom",
        protects: "Energy and the freedom to refuse arrive together.",
        tradeoff: "The drive can feel like pressure to someone already wary.",
      },
    ],
  },
  {
    id: "sorry-not-smaller",
    contentVersion: 2,
    number: "09",
    title: "Sorry, not smaller",
    provenance: "Original Verbalore",
    craft: ["apology", "accountability", "self-respect"],
    size: "deep",
    minutes: 12,
    context: "Repairing a missed check that cost a colleague two hours",
    skill: "Taking specific responsibility, apologising, and repairing without self-condemnation",
    route: { intention: "own-a-mistake", relationship: "work-peer", risk: "self-erasure" },
    landingHazard: {
      label: "An apology can become a demand for reassurance",
      accidentalReading: "When self-condemnation takes over, the person who was harmed may feel required to comfort you instead of receiving repair.",
      deliveryCue: "Stress what you did and what you will repair. Keep your character out of the sentence.",
      saferAlternative: "I missed the check, and that cost you time. I’m sorry. I will repair it today.",
    },
    situation:
      "You missed a final check, and a colleague spent two hours redoing work because of it. You need to name what happened, apologise, and complete the remaining repair today without collapsing into self-blame that makes them comfort you.",
    tangledDraft:
      "I’m so sorry. I feel terrible about this, and I hope you can forgive me.",
    draftDiagnosis:
      "The remorse may be sincere, but the sentence asks the injured person to respond to your feelings. Name what you did, acknowledge its cost, and state the repair before asking for forgiveness.",
    intention: "Own the missed check, apologise for the two hours it cost your colleague, and complete the remaining repair today.",
    invariant: "You missed the check, it cost the colleague two hours, you are sorry, and you will finish the repair today. You do not ask them to manage your guilt.",
    concept: "A real apology attends to the harm, not your guilt. Grovelling asks the injured person to manage your remorse as well.",
    practiceCue: "Read each line and ask who it comforts. If it comforts you, cut it. The apology is for them.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I missed the final check, and you lost two hours fixing it. I’m sorry. I’ll complete the remaining repair today.",
        landing: "Total and clean",
        protects: "Responsibility is complete and unqualified.",
        tradeoff: "The brevity can read as unmoved if your tone is flat.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I missed the check, and that left you with two hours of rework. I’m sorry. I’ll finish the repair today and send you the corrected version.",
        landing: "Owned and understood",
        protects: "Ownership and their experience both get named.",
        tradeoff: "Naming their side risks sounding like you’re managing them.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "This was on me. I’m sorry you lost two hours to it. I’ll take the remaining repair and finish it today.",
        landing: "Repair, not penance",
        protects: "Accountability points at repair, not self-punishment.",
        tradeoff: "Refusing to grovel can read as too composed to someone who wants remorse.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "I missed the check. I’m sorry for the two hours it cost you. I’ll complete the repair today.",
        landing: "Plain apology",
        protects: "The apology stands without spectacle.",
        tradeoff: "Its plainness can seem to underweight the harm.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "My miss cost you two hours. I’m sorry. I’ll finish the repair today.",
        landing: "Owned in a breath",
        protects: "Ownership and the next step in one breath.",
        tradeoff: "The compression can feel like closing the subject too fast.",
      },
    ],
  },
  {
    id: "cut-to-the-point",
    contentVersion: 2,
    number: "10",
    title: "Cut to the point",
    provenance: "Original Verbalore",
    craft: ["compression", "concision", "signal over padding"],
    size: "quick",
    minutes: 3,
    context: "Saying something in far fewer words",
    skill: "Cutting the excess while keeping the whole point",
    route: { intention: "be-concise", relationship: "group", risk: "lost-meaning" },
    landingHazard: {
      label: "Compression can cut away the decision",
      accidentalReading: "A shorter sentence is not clearer if it removes who must act, what must change, or why the point matters.",
      deliveryCue: "Keep the decisive noun and verb. Cut repeated setup before you cut the action.",
      saferAlternative: "Name the decision first, then keep only the reason it depends on.",
    },
    situation:
      "You need to tell the team the deadline moved to Thursday and the review is cancelled, and your draft has grown into three paragraphs. You want to say the same thing in a fraction of the words without losing what matters.",
    tangledDraft:
      "So I wanted to flag that after some back and forth we’ve decided to shift a few things around, and the upshot, if it helps, is that the timing has moved a little and we’re also going to reconsider one of the steps we usually run.",
    draftDiagnosis:
      "This is recognisable workplace prose, but it hides both decisions behind process language. The reader still has to excavate the new deadline and infer that the review is cancelled.",
    intention: "Tell the team the deadline is now Thursday and the review is off, in as few words as it takes.",
    invariant: "Both facts survive: Thursday, and no review. Only the excess is cut.",
    concept: "Length is not thoroughness. The extra words usually protect the writer, not the reader.",
    practiceCue: "Count the words in your draft, then in each line. Check that the shortest version still carries both facts.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "Deadline’s Thursday now. Review’s cancelled.",
        landing: "Both facts, no clutter",
        protects: "Both facts land with zero clutter.",
        tradeoff: "No context for anyone who needs the why.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "Quick change: Thursday’s the new deadline, and we’re skipping the review.",
        landing: "Gentle on-ramp",
        protects: "Brevity with a soft opening.",
        tradeoff: "The opener adds a few words that the direct version omits.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "New plan: the deadline is Thursday, and we’re skipping this review. Thank you for adjusting.",
        landing: "Tight and sure",
        protects: "Both facts arrive with acknowledgment of the team's adjustment.",
        tradeoff: "The thanks adds length when the reader only needs the update.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "Deadline moves to Thursday. No review this round.",
        landing: "Plain and complete",
        protects: "Plain, complete, and easy to scan.",
        tradeoff: "The flatness gives no signal of how big the change is.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Thursday deadline. Review cancelled.",
        landing: "Maximum speed",
        protects: "Maximum speed with both facts intact.",
        tradeoff: "Too terse for readers who’ll want the reasoning.",
      },
    ],
  },
  {
    id: "on-the-record",
    contentVersion: 2,
    number: "11",
    title: "On the record",
    provenance: "Original Verbalore",
    craft: ["principled dissent", "on-record", "cooperation without consent"],
    size: "deep",
    minutes: 11,
    context: "Objecting when a hiring panel abandons its agreed scoring rubric",
    skill: "Recording a principled objection and naming the ethical limit of cooperation",
    route: { intention: "hold-ground", relationship: "group", risk: "hostility" },
    landingHazard: {
      label: "Recorded dissent can sound like future point-scoring",
      accidentalReading: "The room may hear I told you so in advance if the objection is framed as personal vindication rather than a maintained concern.",
      deliveryCue: "State the objection once, separate it from cooperation, and avoid theatrical emphasis on the record.",
      saferAlternative: "I object to setting aside the agreed rubric. Please record that; I will help the panel apply it fairly.",
    },
    situation:
      "A hiring panel wants to discard its agreed scoring rubric and choose a candidate on an undefined sense of fit. You believe that invites bias. The chair wants to proceed today. You want your objection recorded and will help apply the rubric fairly, but you will not describe an unscored choice as criteria-based.",
    tangledDraft:
      "I disagree with the decision, but I’ll support whatever the team wants.",
    draftDiagnosis:
      "This sounds cooperative, but whatever the team wants makes the objection disposable and leaves no durable record of the abandoned rubric. State what you oppose, what fair process you will support, and what you will not misrepresent.",
    intention: "Record your objection to abandoning the rubric, support a fair scoring process, and refuse to misrepresent an unscored choice.",
    invariant: "The objection to discarding the rubric is recorded. You will help apply it fairly and will not call an unscored choice criteria-based.",
    concept: "Principled dissent names both the objection and the boundary of cooperation. Loyalty cannot require misrepresentation.",
    practiceCue: "Separate the sentence that cooperates from the one that objects. Say both, and let neither cancel the other.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I want it recorded that I object to discarding the agreed rubric. I’ll help rerun the scoring, but I won’t present an unscored choice as criteria-based.",
        landing: "On record, no fight",
        protects: "The objection is documented without a fight.",
        tradeoff: "Going on record can read as adversarial even when it isn’t.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I understand the panel wants a decision today, but I can’t support setting aside the rubric. Please record that; I’ll help us apply it now.",
        landing: "Cooperate, not consent",
        protects: "Cooperation and dissent are cleanly separated.",
        tradeoff: "The concession can dilute how seriously the objection is taken.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I want us to make a defensible appointment. My objection to setting aside the rubric remains, and I’ll help us score the candidates fairly today.",
        landing: "Loyal and dissenting",
        protects: "Loyalty and dissent hold together.",
        tradeoff: "The warmth can make the objection easy to wave through.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "For the record, I oppose selecting outside the agreed rubric.",
        landing: "Quiet marker",
        protects: "A quiet marker that costs the room nothing.",
        tradeoff: "The understatement makes it easy to overlook later.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Use the rubric, or record my objection.",
        landing: "Logged in one stroke",
        protects: "The dissent is logged in a single stroke.",
        tradeoff: "The sharpness can feel aimed at people, not the plan.",
      },
    ],
  },
  {
    id: "light-and-still-standing",
    contentVersion: 2,
    number: "12",
    title: "Light, and still standing",
    provenance: "Original Verbalore",
    craft: ["wit", "poise", "de-escalation"],
    size: "quick",
    minutes: 4,
    context: "Answering a jab without escalating",
    skill: "Using wit to hold your ground while keeping the tone light",
    route: { intention: "answer-pressure", relationship: "work-peer", risk: "sarcasm" },
    landingHazard: {
      label: "Deliberate wit can tip into sarcasm",
      accidentalReading: "A line meant to release tension can sound like a disguised insult if the smile, timing, or shared warmth is absent.",
      deliveryCue: "Let the room hear that you include yourself in the joke. If the line only works coldly, do not use it.",
      saferAlternative: "True, especially when one step prevents three rounds of rework.",
    },
    situation:
      "You propose one final review to prevent repeated rework. A colleague jokes, ‘You do enjoy adding steps.’ You want to answer lightly while preserving the point that one review now prevents several rescues later.",
    tangledDraft:
      "Fair point, but some of us have to think about the rework.",
    draftDiagnosis:
      "The line tries to be light, but some of us quietly divides the room into responsible and irresponsible people. Keep the wit aimed at the arithmetic of rework, not at the colleague.",
    intention: "Answer the jab lightly while preserving the case for one review now to prevent repeated rework later.",
    invariant: "The review remains necessary because it prevents more work later. The answer stays light, not hostile.",
    concept: "Wit is control, not cruelty. The best line leaves everyone free to laugh, including the person you answered.",
    practiceCue: "Deliver each comeback with a smile in your voice. If it only works said coldly, it’s a jab, not wit.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "Only the steps that prevent three rounds of rework.",
        landing: "Playful hit",
        protects: "You answer squarely while keeping it playful.",
        tradeoff: "Even when delivered lightly, a direct answer can raise the temperature.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "Only the steps that save us walking backwards later.",
        landing: "Deflected, not conceded",
        protects: "You deflect without conceding the point.",
        tradeoff: "The softness can let the jab stand unanswered.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I do, when one review saves everyone three rescues.",
        landing: "Generous and firm",
        protects: "Generosity and confidence in the same breath.",
        tradeoff: "Agreeing first can blur that you held your ground.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "One review now, or several rescues later.",
        landing: "Unbothered",
        protects: "Minimal, unbothered, and still standing.",
        tradeoff: "The understatement can read as not engaging at all.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "One review. Three rescues avoided.",
        landing: "The arithmetic answers",
        protects: "The comeback and the point land in one compact contrast.",
        tradeoff: "The snap can tip into sharp if the room’s already tense.",
      },
    ],
  },
];

export const ALL_EXPRESSION_STUDIES: ExpressionStudy[] = [
  BOUNDARY_STUDY,
  ...EXPRESSION_STUDIES,
];

export const EXPRESSION_STUDY_BY_ID = new Map(
  ALL_EXPRESSION_STUDIES.map((study) => [study.id, study]),
);
